import { useAuthStore } from "@features/authentication/stores/authStore";
import { PaywallModalShell } from "@features/payment/components/PaywallModalShell";
import { useCompleteEventUnlock } from "@features/payment/hooks/useCompleteEventUnlock";
import { buildPaymentFailedPath, buildPaymentSuccessPath } from "@features/payment/paymentResultNavigation";
import type { ChargeResult } from "@infrastructure/payments/types";
import useCurrentUser from "@shared/queries/useCurrentUser";
import clsx from "clsx";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import PayPalCheckoutButtons from "./PayPalCheckoutButtons";

const PAYMENT_PRODUCT = "event_vip_unlock" as const;

function vipPricePence(): number {
  const v = import.meta.env.VITE_EVENT_VIP_PRICE_GBP_PENCE;
  const raw = typeof v === "string" ? Number.parseInt(v, 10) : Number.NaN;
  return Number.isFinite(raw) && raw > 0 ? raw : 499;
}

function chargeAmountPence(result: ChargeResult, fallback: number): number {
  return result.success ? result.amountCapturedCents : fallback;
}

export type VipLikedYouPaywallModalProps = {
  open?: boolean;
};

export function VipLikedYouPaywallModal({ open = true }: VipLikedYouPaywallModalProps) {
  "use no memo";

  const { userId } = useAuthStore();
  const { user } = useCurrentUser();
  const completeUnlock = useCompleteEventUnlock();
  const navigate = useNavigate();
  const [banner, setBanner] = useState<string | null>(null);
  const [isUnlocking, setIsUnlocking] = useState(false);

  const clientId =
    typeof import.meta.env.VITE_PAYPAL_CLIENT_ID === "string" ? import.meta.env.VITE_PAYPAL_CLIENT_ID : "";

  const metadata = useMemo(() => {
    if (!userId || !user?.lastKnownEventId) {
      return { source: "liked_you_vip_paywall", product: PAYMENT_PRODUCT };
    }
    return {
      source: "liked_you_vip_paywall",
      product: PAYMENT_PRODUCT,
      userId,
      eventId: user.lastKnownEventId,
    };
  }, [userId, user?.lastKnownEventId]);

  const goDiscover = () => {
    navigate("/discover");
  };

  const finishSuccessNavigation = (amountPence: number, unlockPending: boolean) => {
    navigate(
      buildPaymentSuccessPath({
        product: PAYMENT_PRODUCT,
        amountPence,
        next: "/liked-you",
        unlockPending,
      }),
      { replace: true }
    );
  };

  const finishFailedNavigation = (amountPence: number, error?: string) => {
    navigate(
      buildPaymentFailedPath({
        amountPence,
        product: PAYMENT_PRODUCT,
        next: "/liked-you",
        ...(error ? { error } : {}),
      }),
      { replace: true }
    );
  };

  const applyUnlock = async (amountPence: number) => {
    if (!user?.lastKnownEventId) {
      setBanner("Missing event context. Re-enter via event QR and try again.");
      return;
    }
    setIsUnlocking(true);
    try {
      await completeUnlock({ grantProfileBoost: true });
      finishSuccessNavigation(amountPence, false);
    } catch {
      finishSuccessNavigation(amountPence, true);
    } finally {
      setIsUnlocking(false);
    }
  };

  const handleChargeResult = async (result: ChargeResult) => {
    const amountPence = chargeAmountPence(result, vipPricePence());
    if (!result.success) {
      finishFailedNavigation(amountPence, result.message);
      return;
    }
    await applyUnlock(amountPence);
  };

  if (!open) {
    return null;
  }

  return (
    <PaywallModalShell open={open} testId="vip-liked-you-paywall-modal" title="VIP — see who liked you tonight">
      <p className="mt-3 text-center font-mono text-[length:var(--font-size-body-medium)] font-semibold leading-6 text-[#606A85]">
        Liked you is VIP-only for this event. Pay once at the VIP rate to see who liked you and unlock full liked-you
        access for tonight, with an optional profile boost.
      </p>
      {banner ? (
        <p className="mt-4 rounded-md bg-amber-100 px-3 py-2 text-center text-sm text-amber-800">{banner}</p>
      ) : null}
      {!user?.lastKnownEventId ? (
        <p className="mt-4 rounded-md bg-amber-100 px-3 py-2 text-center text-sm text-amber-800">
          No active event on your profile. Re-enter through the event QR code.
        </p>
      ) : null}
      {isUnlocking ? (
        <p className="mt-4 text-center text-sm text-gray-600" data-testid="vip-liked-you-unlocking">
          Unlocking…
        </p>
      ) : null}

      <div className="mt-6">
        <PayPalCheckoutButtons
          clientId={clientId}
          currency="gbp"
          data-testid="vip-liked-you-paywall-buttons"
          getAmountCents={vipPricePence}
          metadata={metadata}
          onChargeResult={handleChargeResult}
          onErrorBanner={setBanner}
        />
      </div>

      <div className="mt-5 flex flex-col gap-2 border-t border-[var(--color-border-default)] pt-5">
        <button
          type="button"
          onClick={goDiscover}
          className={clsx(
            "flex h-14 w-full items-center justify-center rounded-button-lg",
            "border-2 border-[var(--color-border-card)] font-mono text-[length:var(--font-size-title-medium)] font-semibold text-[#606A85]",
            "transition-colors hover:bg-black/5",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
          )}
          data-testid="vip-liked-you-back-discover"
        >
          Continue to Discover
        </button>
      </div>
    </PaywallModalShell>
  );
}
